import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ActivateUserDocument,
  type ActivateUserMutation,
  type ActivateUserMutationVariables,
  DeactivateUserDocument,
  type DeactivateUserMutation,
  type DeactivateUserMutationVariables,
  GetUserDetailDocument,
  GetUsersDocument,
} from "~/features/admin/users/gqls";
import { translateError } from "~/lib/errors";

interface UserActivationTarget {
  id: string;
}

/**
 * Activate / deactivate a user. Returns plain submitters and a `processing`
 * flag; the calling detail view owns the confirmation dialog (V1 parity).
 */
export function useUserActivation() {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const [deactivateUser] = useMutation<DeactivateUserMutation, DeactivateUserMutationVariables>(DeactivateUserDocument);
  const [activateUser] = useMutation<ActivateUserMutation, ActivateUserMutationVariables>(ActivateUserDocument);

  const onActivate = useCallback(
    async ({ id }: UserActivationTarget) => {
      setProcessing(true);
      try {
        await activateUser({
          variables: { id },
          refetchQueries: [GetUsersDocument, { query: GetUserDetailDocument, variables: { userId: id } }],
        });
        toast.success(t("feature.common.activatedSuccessfully", { model: "user" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [activateUser, t],
  );

  const onDeactivate = useCallback(
    async ({ id }: UserActivationTarget) => {
      setProcessing(true);
      try {
        await deactivateUser({
          variables: { id },
          refetchQueries: [GetUsersDocument, { query: GetUserDetailDocument, variables: { userId: id } }],
        });
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "user" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [deactivateUser, t],
  );

  return { onActivate, onDeactivate, processing };
}
